import React, { useState, useEffect, ReactNode } from 'react';
import useSwipeNavigation from '@/hooks/useSwipeNavigation';
import { cn } from "@/lib/utils";

interface AppShellProps {
  header?: ReactNode;
  tabBar?: ReactNode;
  children: ReactNode;
  className?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  onRefresh?: () => void;
  title?: string;
  enableSwipeBack?: boolean;
  isLoading?: boolean;
}

export const AppShell: React.FC<AppShellProps> = ({
  header,
  tabBar,
  children,
  className,
  showBackButton = false,
  onBack,
  onRefresh,
  title,
  enableSwipeBack = true,
  isLoading = false,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);
  const [pullMoveY, setPullMoveY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const threshold = 80; // Pixel threshold to activate refresh

  useSwipeNavigation({
    enableBackNavigation: enableSwipeBack && showBackButton,
    onSwipeRight: showBackButton ? onBack : undefined,
  });

  // Pull to refresh logic
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // Only activate if we're at the top of the page
      if (window.scrollY === 0) {
        setPullStartY(e.touches[0].clientY);
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isPulling) {
        setPullMoveY(e.touches[0].clientY);
      }
    };

    const handleTouchEnd = () => {
      if (isPulling && pullMoveY - pullStartY > threshold && onRefresh) {
        setRefreshing(true);
        // Call the refresh function
        Promise.resolve(onRefresh()).finally(() => {
          setTimeout(() => setRefreshing(false), 800);
        });
      }
      setIsPulling(false);
      setPullStartY(0);
      setPullMoveY(0);
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, pullStartY, pullMoveY, onRefresh, threshold]);

  // Calculate pull distance
  const pullDistance = isPulling ? Math.max(0, Math.min(pullMoveY - pullStartY, threshold)) : 0;
  const pullProgress = pullDistance / threshold;

  return (
    <div className={cn(
      "flex flex-col min-h-screen w-full bg-background relative overflow-hidden",
      className
    )}>
      {/* Pull to refresh indicator */}
      {(pullDistance > 0 || refreshing) && (
        <div 
          className={cn(
            "absolute top-0 left-0 w-full flex justify-center items-center z-50 transition-transform",
            refreshing ? "h-16 translate-y-0" : "h-8"
          )}
          style={{ 
            transform: !refreshing ? `translateY(${pullDistance - 20}px)` : undefined,
            opacity: refreshing ? 1 : pullProgress 
          }}
        >
          <div className={cn(
            "w-6 h-6 border-2 border-t-transparent rounded-full",
            refreshing ? "animate-spin border-primary" : "border-gray-300"
          )} />
        </div>
      )}

      {/* Header section */}
      {header && (
        <header className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-sm border-b">
          {header}
        </header>
      )}
      
      {/* Main content */}
      <main className={cn(
        "flex-1 w-full h-full",
        tabBar ? "pb-14" : "pb-0",
        isLoading ? "opacity-50" : ""
      )}>
        {children}
      </main>
      
      {/* Bottom tab bar */}
      {tabBar && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t">
          {tabBar}
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-50">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default AppShell; 